import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, User, BookOpen, Award, ArrowLeft, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { publicAPI } from '../lib/api';

const MILESTONE_STATUS = {
  pending:     { color: '#6b7280', label: 'Pending' },
  in_progress: { color: '#3b82f6', label: 'In Progress' },
  submitted:   { color: '#f59e0b', label: 'Submitted' },
  approved:    { color: '#22c55e', label: 'Approved' },
  rejected:    { color: '#ef4444', label: 'Needs Revision' },
};

const S = {
  page:    { minHeight: '100vh', background: '#0D1117', color: '#e2e8f0', fontFamily: 'DM Sans, sans-serif' },
  nav:     { borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  navLink: { display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280', fontSize: '0.875rem', textDecoration: 'none' },
  hero:    { borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '60px 40px 40px', maxWidth: 960, margin: '0 auto' },
  body:    { maxWidth: 960, margin: '0 auto', padding: '40px 40px 80px', display: 'grid', gridTemplateColumns: '1fr 320px', gap: 48 },
  h1:      { fontFamily: 'Syne, sans-serif', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: 24, color: '#f1f5f9' },
  h2:      { fontFamily: 'Syne, sans-serif', fontSize: '1.05rem', fontWeight: 600, marginBottom: 14, color: '#f1f5f9' },
  eyebrow: { fontFamily: 'DM Mono, monospace', fontSize: '0.7rem', color: '#2A9D8F', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 16 },
  section: { marginBottom: 36 },
  tag:     { display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: '0.72rem', fontFamily: 'DM Mono, monospace', background: 'rgba(42,157,143,0.1)', color: '#2A9D8F', border: '1px solid rgba(42,157,143,0.2)', marginRight: 6, marginBottom: 6 },
  card:    { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 24 },
};

export default function ShowcasePage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const showcaseUrl = typeof window !== 'undefined' ? window.location.href : `https://futurumawards.com/showcase/${slug}`;

  useEffect(() => {
    publicAPI.getShowcase(slug)
      .then(r => setProject(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleShare = () => {
    navigator.clipboard.writeText(showcaseUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#2A9D8F', fontSize: '0.9rem' }}>Loading project…</div>
    </div>
  );

  if (notFound) return (
    <div style={{ ...S.page, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '2rem', color: '#C9A84C' }}>404</div>
      <div style={{ color: '#6b7280' }}>This project showcase is not available.</div>
      <Link to="/" style={{ color: '#2A9D8F', fontSize: '0.875rem', textDecoration: 'none' }}>← Back to Futurum</Link>
    </div>
  );

  const milestones = Array.isArray(project.milestones) ? project.milestones : [];
  const completed = milestones.filter(m => m.status === 'approved').length;
  const progress = milestones.length ? Math.round((completed / milestones.length) * 100) : 0;
  const tools = Array.isArray(project.tools_used) ? project.tools_used : [];

  const duration = (() => {
    if (!project.start_date && !project.end_date) return null;
    const fmt = (d) => new Date(d).toLocaleDateString('en-NG', { month: 'short', year: 'numeric' });
    if (project.start_date && project.end_date) return `${fmt(project.start_date)} — ${fmt(project.end_date)}`;
    if (project.start_date) return `Started ${fmt(project.start_date)}`;
    return null;
  })();

  return (
    <div style={S.page}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700&family=DM+Sans:wght@400;500&family=DM+Mono:wght@400&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={S.nav}>
        <Link to="/" style={S.navLink}><ArrowLeft size={16} /> Futurum Innovation Awards</Link>
        <button onClick={handleShare} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#e2e8f0', cursor: 'pointer', fontSize: '0.8rem' }}>
          <Share2 size={14} /> {copied ? 'Link copied!' : 'Share'}
        </button>
      </nav>

      {/* Hero */}
      <div style={S.hero}>
        {project.domain && <div style={S.eyebrow}>{project.domain}</div>}
        <h1 style={S.h1}>{project.topic_title || project.title}</h1>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '0.85rem', color: '#6b7280', marginBottom: 24 }}>
          {project.student_name && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <User size={14} /> {project.student_name}
              {project.department && ` · ${project.department}`}
            </span>
          )}
          {project.lecturer_name && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <BookOpen size={14} /> Supervised by {project.lecturer_name}
              {project.lecturer_dept && ` · ${project.lecturer_dept}`}
            </span>
          )}
          {project.sponsor_name && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#C9A84C' }}>
              Sponsored by {project.sponsor_name}
            </span>
          )}
          {duration && <span style={{ color: '#6b7280' }}>📅 {duration}</span>}
        </div>

        {/* Verified badge */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'rgba(42,157,143,0.1)', borderRadius: 999, border: '1px solid rgba(42,157,143,0.25)', fontSize: '0.75rem', color: '#2A9D8F', marginBottom: 20 }}>
          <CheckCircle size={13} /> Verified Futurum Innovation Awards · Season II · OAU
        </div>

        {project.is_winner && (
          <div style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', background: 'rgba(201,168,76,0.12)', borderRadius: 999, border: '1px solid rgba(201,168,76,0.3)' }}>
            <Award size={14} style={{ color: '#C9A84C' }} />
            <span style={{ color: '#C9A84C', fontWeight: 500, fontSize: '0.8rem' }}>
              {project.award_category || 'Award Winner'}{project.rank ? ` · Rank #${project.rank}` : ''}
            </span>
          </div>
        )}

        {/* Progress bar */}
        {milestones.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280', marginBottom: 8 }}>
              <span>Project progress</span>
              <span>{completed} / {milestones.length} milestones complete</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#1D9E75,#2A9D8F)', borderRadius: 4, transition: 'width 0.6s ease' }} />
            </div>
          </div>
        )}
      </div>

      <div style={S.body}>
        {/* Left column */}
        <div>
          {/* Abstract */}
          {project.abstract && (
            <div style={S.section}>
              <h2 style={S.h2}>Project abstract</h2>
              <p style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '0.9rem' }}>{project.abstract}</p>
            </div>
          )}

          {/* About */}
          {project.topic_description && (
            <div style={S.section}>
              <h2 style={S.h2}>About this project</h2>
              <p style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '0.9rem' }}>{project.topic_description}</p>
            </div>
          )}

          {/* Outcomes */}
          {project.public_summary && (
            <div style={S.section}>
              <h2 style={S.h2}>Outcomes & findings</h2>
              <p style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '0.9rem' }}>{project.public_summary}</p>
            </div>
          )}

          {/* Tools */}
          {tools.length > 0 && (
            <div style={S.section}>
              <h2 style={S.h2}>Technologies & tools</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tools.map((t, i) => <span key={i} style={S.tag}>{t}</span>)}
              </div>
            </div>
          )}

          {/* Milestones */}
          {milestones.length > 0 && (
            <div style={S.section}>
              <h2 style={S.h2}>Project milestones</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {milestones.map((m, i) => {
                  const mCfg = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.pending;
                  return (
                    <div key={i} style={{ padding: '12px 16px', borderRadius: 10, background: m.status === 'approved' ? 'rgba(34,197,94,0.05)' : 'rgba(255,255,255,0.02)', border: `1px solid ${m.status === 'approved' ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: m.status === 'approved' ? '#22c55e' : 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, color: m.status === 'approved' ? '#fff' : '#6b7280' }}>
                          {m.status === 'approved' ? <CheckCircle size={13} /> : i + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#e2e8f0' }}>{m.title}</div>
                          {m.submission_notes && m.status === 'approved' && (
                            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{m.submission_notes}</div>
                          )}
                        </div>
                        <span style={{ fontSize: '0.7rem', color: mCfg.color, fontFamily: 'DM Mono, monospace' }}>{mCfg.label}</span>
                      </div>
                      {m.submission_url && m.status === 'approved' && (
                        <div style={{ marginTop: 10, paddingLeft: 36 }}>
                          <img src={m.submission_url} alt="Milestone" style={{ maxWidth: 220, maxHeight: 150, borderRadius: 8, objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => window.open(m.submission_url, '_blank')}
                            onError={e => { e.target.style.display = 'none'; }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div>
          <div style={{ ...S.card, position: 'sticky', top: 24 }}>
            <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.65rem', color: '#6b7280', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Project details</div>

            {[
              { label: 'Season',     value: project.season },
              { label: 'Domain',     value: project.domain },
              { label: 'Department', value: project.department || project.lecturer_dept },
              { label: 'Faculty',    value: 'Faculty of Technology' },
              { label: 'University', value: 'Obafemi Awolowo University' },
              { label: 'Supervisor', value: project.lecturer_name },
              { label: 'Sponsor',    value: project.sponsor_name },
              { label: 'Duration',   value: duration },
              { label: 'Status',     value: project.status?.replace(/_/g, ' ') },
            ].filter(r => r.value).map((row, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '0.8rem' }}>
                <span style={{ color: '#6b7280' }}>{row.label}</span>
                <span style={{ color: '#e2e8f0', fontWeight: 500, textAlign: 'right', maxWidth: 170, textTransform: row.label === 'Status' ? 'capitalize' : 'none' }}>{row.value}</span>
              </div>
            ))}

            {/* QR Code */}
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.65rem', color: '#6b7280', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Scan to verify</div>
              <div style={{ background: '#fff', padding: 12, borderRadius: 10 }}>
                <QRCodeSVG value={showcaseUrl} size={140} bgColor="#ffffff" fgColor="#0D1117" level="M" />
              </div>
              <div style={{ fontSize: '0.7rem', color: '#4b5563', textAlign: 'center', lineHeight: 1.5 }}>
                Scan with any camera to open this project page
              </div>
            </div>

            {/* Verified notice */}
            <div style={{ marginTop: 20, padding: '12px 14px', background: 'rgba(42,157,143,0.08)', borderRadius: 8, border: '1px solid rgba(42,157,143,0.2)', fontSize: '0.75rem', color: '#2A9D8F', lineHeight: 1.6 }}>
              This project is verified by the Futurum Innovation Awards programme at Obafemi Awolowo University, Ile-Ife.
            </div>

            <a href="https://futurumawards.com" target="_blank" rel="noreferrer"
              style={{ display: 'block', marginTop: 14, textAlign: 'center', padding: '10px', background: '#C9A84C', borderRadius: 8, color: '#0D1117', fontWeight: 600, fontSize: '0.82rem', textDecoration: 'none' }}>
              Visit futurumawards.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
